import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Container,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Chip,
  Stack,
  Alert,
} from '@mui/material';
import { ThemeProvider, useTheme } from '@mui/material/styles';
import { resolveEventTheme, eventSurfaceSx } from '../eventThemes';
import { CheckCircleOutline, CalendarToday, LocationOn } from '@mui/icons-material';
import { sanitizeHtml } from '../utils/sanitize';
import { Event, Rsvp } from '../types';
import EventNavigation from './EventNavigation';

interface RsvpEditDetails extends Rsvp {
  attendee_email?: string;
  event: Event;
  claimed_items: string[];
}

const invalidLinkMessage = 'Failed to load RSVP details. The link may be invalid or expired.';
const parseItems = (value: string[] | string | undefined): string[] => {
  const items = typeof value === 'string' ? (value ? JSON.parse(value) : []) : value;
  return Array.isArray(items) ? items.filter((item): item is string => typeof item === 'string') : [];
};


interface RSVPFormData {
  name: string;
  email_address: string;
  attending: string;
  bringing_guests: string;
  guest_count: number;
  guest_names: string[];
  items_bringing: string[];
  other_items: string;
}

const RSVPEditForm: React.FC = () => {
  const { slug, editId } = useParams<{ slug: string; editId: string }>();
  const baseTheme = useTheme();
  const [formData, setFormData] = useState<RSVPFormData>({
    name: '',
    email_address: '',
    attending: '',
    bringing_guests: '',
    guest_count: 1,
    guest_names: [],
    items_bringing: [],
    other_items: ''
  });
  const [neededItems, setNeededItems] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [event, setEvent] = useState<Event | null>(null);
  const [isEventClosed, setIsEventClosed] = useState(false);
  const [isItemsSelectOpen, setIsItemsSelectOpen] = useState(false);

  const eventTheme = useMemo(() => resolveEventTheme(baseTheme, event || {}), [event, baseTheme]);

  const wpPos = (event?.wallpaper_position || '50% 50%').split(/\s+/);
  const wpX = Number.isFinite(parseFloat(wpPos[0])) ? parseFloat(wpPos[0]) : 50;
  const wpY = Number.isFinite(parseFloat(wpPos[1])) ? parseFloat(wpPos[1]) : 50;
  const wpZoom = event?.wallpaper_zoom ?? 1;

  const rootSx = {
    ...eventSurfaceSx(event), minHeight: '100vh',
  };

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    setEvent(null);
    setSuccess(false);
    const fetchRsvpDetails = async () => {
      try {
        // This capability authorizes this RSVP only. Event/list APIs still need
        // an invitation, so all editing data comes from this one endpoint.
        const { data } = await axios.get<RsvpEditDetails>(`/api/rsvps/edit/${encodeURIComponent(editId || '')}`);
        if (!active) return;
        if (!data?.event || data.event.slug !== slug || !Array.isArray(data.claimed_items)) {
          throw new Error('Failed to fetch data from server');
        }
        const selectedItems = parseItems(data.items_bringing);
        const claimed = new Set(data.claimed_items);
        setNeededItems(parseItems(data.event.needed_items).filter(item => !claimed.has(item) || selectedItems.includes(item)));
        setIsEventClosed(Boolean(data.event.rsvp_cutoff_date && new Date() > new Date(data.event.rsvp_cutoff_date)));
        setFormData({
          name: data.name,
          email_address: data.attendee_email || '',
          attending: data.attending,
          bringing_guests: data.bringing_guests,
          guest_count: data.guest_count,
          guest_names: parseItems(data.guest_names),
          items_bringing: selectedItems,
          other_items: data.other_items || ''
        });
        setEvent(data.event);
      } catch {
        if (active) setError(invalidLinkMessage);
      } finally {
        if (active) setLoading(false);
      }
    };
    fetchRsvpDetails();
    return () => { active = false; };
  }, [slug, editId]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;

    if (name === 'attending') {
      if (value === 'no' || value === 'maybe') {
        setFormData(prev => ({
          ...prev, [name]: value,
          bringing_guests: 'no', guest_count: 0, guest_names: [],
          items_bringing: [], other_items: ''
        }));
        return;
      }
    }

    if (name === 'bringing_guests') {
      if (value === 'no') {
        setFormData(prev => ({ ...prev, [name]: value, guest_count: 0, guest_names: [] }));
        return;
      } else if (value === 'yes') {
        setFormData(prev => ({ ...prev, [name]: value, guest_count: 1, guest_names: [''] }));
        return;
      }
    }

    if (name === 'guest_count') {
      const count = parseInt(value) || 0;
      setFormData(prev => ({
        ...prev, [name]: count,
        guest_names: Array(count).fill('').map((_, i) => prev.guest_names[i] || '')
      }));
      return;
    }

    if (name.startsWith('guest_name_')) {
      const index = parseInt(name.split('_')[2]);
      setFormData(prev => ({
        ...prev,
        guest_names: prev.guest_names.map((n, i) => i === index ? value : n)
      }));
      return;
    }

    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const { name, value } = e.target;

    if (name === 'attending' && value !== 'yes') {
      setFormData(prev => ({
        ...prev, attending: value as 'no' | 'maybe',
        bringing_guests: 'no', guest_count: 0, guest_names: [],
        items_bringing: [], other_items: ''
      }));
    } else if (name === 'bringing_guests') {
      setFormData(prev => {
        const maxGuests = event?.max_guests_per_rsvp;
        let initialGuestCount = 1;
        if (maxGuests === 0 && value === 'yes') {
          return { ...prev, bringing_guests: 'no', guest_count: 0, guest_names: [] };
        }
        if (maxGuests !== undefined && maxGuests !== -1 && maxGuests < initialGuestCount) {
          initialGuestCount = maxGuests;
        }
        return {
          ...prev, bringing_guests: value as 'yes' | 'no',
          guest_count: value === 'yes' ? initialGuestCount : 0,
          guest_names: value === 'no' ? [] : Array(initialGuestCount).fill('')
        };
      });
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleItemsChange = (e: SelectChangeEvent<string[]>) => {
    const { value } = e.target;
    setFormData(prev => ({ ...prev, items_bringing: Array.isArray(value) ? value : [] }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isEventClosed) {
      setError('Event registration is closed. Changes are not allowed.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    if (!formData.name.trim() || !formData.email_address.trim() || !formData.attending) {
      setError('Please fill in all required fields');
      setIsSubmitting(false);
      return;
    }

    if (formData.attending === 'yes' && !formData.bringing_guests) {
      setError('Please indicate if you are bringing guests');
      setIsSubmitting(false);
      return;
    }

    if (formData.bringing_guests === 'yes' &&
        (formData.guest_count < 1 || formData.guest_names.some(name => !name.trim()))) {
      setError('Please provide names for all guests');
      setIsSubmitting(false);
      return;
    }

    try {
      const splitOtherItems = formData.other_items
        .split(/\r?\n|,/).map(s => s.trim()).filter(Boolean).join(', ');
      const submissionData = {
        ...formData,
        items_bringing: formData.items_bringing,
        other_items: splitOtherItems
      };
      await axios.put(`/api/rsvps/edit/${encodeURIComponent(editId || '')}`, submissionData);
      setSuccess(true);
    } catch (err) {
      const response = (err as { response?: { status?: number; data?: { code?: string } } })?.response;
      if (response?.data?.code === 'RSVP_EDIT_INVALID' || response?.status === 404) {
        setEvent(null);
        setError(invalidLinkMessage);
      } else setError('Failed to update RSVP. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography>Loading your RSVP...</Typography>
        </Paper>
      </Box>
    );
  }

  if ((error && !event) || !event) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Alert severity="error">{error || 'Event or RSVP not found'}</Alert>
        </Paper>
      </Box>
    );
  }

  if (success) {
    const successContent = (
      <Box sx={rootSx}>
        <Container maxWidth="sm" sx={{ py: 6 }}>
          <Paper sx={{ p: 5, textAlign: 'center' }}>
            <CheckCircleOutline sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h4" sx={{ mb: 1 }}>
              Updated!
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              Your RSVP has been updated successfully.
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center' }}><EventNavigation slug={slug || ''} page="edit" /></Box>
          </Paper>
        </Container>
      </Box>
    );
    return eventTheme ? <ThemeProvider theme={eventTheme}>{successContent}</ThemeProvider> : successContent;
  }

  const mainContent = (
    <Box sx={rootSx}>
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Box sx={{ mb: 3 }}><EventNavigation slug={slug || ''} page="edit" /></Box>
        {/* Event details hero card */}
        <Paper sx={{ p: 3, mb: 3 }}>
          {event.wallpaper && (
            <Box sx={{ width: '100%', aspectRatio: '16 / 5', overflow: 'hidden', position: 'relative', borderRadius: 2, mb: 2 }}>
              <Box component="img" src={event.wallpaper} alt={event.title} sx={{
                position: 'absolute', left: `${wpX}%`, top: `${wpY}%`,
                transform: `translate(-${wpX}%, -${wpY}%)`,
                width: `${100 * wpZoom}%`, height: 'auto', display: 'block',
              }} />
            </Box>
          )}
          <Typography variant="h4" sx={{ mb: 2 }}>
            {event.title}
          </Typography>
          <Stack spacing={0.75} sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CalendarToday sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0 }} />
              <Typography variant="body2" color="text.secondary">
                {new Date(event.date).toLocaleDateString(undefined, {
                  weekday: 'short', month: 'short', day: 'numeric',
                  year: 'numeric', hour: 'numeric', minute: '2-digit',
                })}
              </Typography>
            </Box>
            {event.location && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocationOn sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0 }} />
                <Typography variant="body2" color="text.secondary">
                  {event.location}
                </Typography>
              </Box>
            )}
          </Stack>
          {event.description && (
            <Box
              sx={{
                color: 'text.secondary',
                fontSize: '0.875rem',
                whiteSpace: 'normal',
                overflowWrap: 'break-word',
                '& *': { maxWidth: '100%' },
                '& p': { margin: 0, lineHeight: 1.6, whiteSpace: 'normal' },
                '& p + p': { marginTop: '0.75em' },
                '& p:empty': { marginTop: '0.5em' },
                '& br': { display: 'block', content: '""', marginTop: '0.5em' },
                '& ul, & ol': { marginLeft: 2, marginTop: '0.5em', marginBottom: '0.5em', paddingLeft: '1.5em' },
                '& li': { marginBottom: 0, lineHeight: 1.6 },
                '& h1, & h2, & h3': { marginTop: '0.75em', marginBottom: '0.5em' },
                '& a': { color: 'primary.main' },
                '& img': { maxWidth: '100%', height: 'auto' },
                '& pre, & code': { whiteSpace: 'pre-wrap', maxWidth: '100%', overflow: 'auto' },
              }}
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(event.description) }}
            />
          )}
          {event.rsvp_cutoff_date && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
              Submissions cannot be edited after {new Date(event.rsvp_cutoff_date).toLocaleDateString(undefined, {
                weekday: 'short', month: 'short', day: 'numeric',
                year: 'numeric', hour: 'numeric', minute: '2-digit',
              })}
            </Typography>
          )}
        </Paper>

        {/* Edit form card */}
        <Paper sx={{ p: 4 }}>
          <Typography variant="h5" sx={{ mb: 3 }}>
            Edit Your RSVP
          </Typography>

          {isEventClosed && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Event registration is closed. Changes are not allowed. Please contact the event organizer for assistance.
            </Alert>
          )}

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <Box component="form" onSubmit={handleSubmit}>
            <Stack spacing={2.5}>
              <TextField
                label="Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                fullWidth
                disabled={isEventClosed}
              />

              <TextField
                label="Email Address"
                name="email_address"
                type="email"
                value={formData.email_address}
                onChange={handleChange}
                required
                fullWidth
                disabled={isEventClosed}
                helperText="If you change your email, a new confirmation will be sent to the new address"
              />

              <FormControl fullWidth required disabled={isEventClosed}>
                <InputLabel>Are you attending?</InputLabel>
                <Select
                  name="attending"
                  value={formData.attending}
                  onChange={handleSelectChange}
                  label="Are you attending?"
                >
                  <MenuItem value="">Select an option</MenuItem>
                  <MenuItem value="yes">Yes</MenuItem>
                  <MenuItem value="no">No</MenuItem>
                </Select>
              </FormControl>

              {formData.attending === 'yes' && (
                <>
                  <FormControl fullWidth required disabled={isEventClosed}>
                    <InputLabel>Are you bringing any guests?</InputLabel>
                    <Select
                      name="bringing_guests"
                      value={formData.bringing_guests}
                      onChange={handleSelectChange}
                      label="Are you bringing any guests?"
                    >
                      <MenuItem value="">Select an option</MenuItem>
                      <MenuItem value="yes">Yes</MenuItem>
                      <MenuItem value="no">No</MenuItem>
                    </Select>
                  </FormControl>

                  {formData.bringing_guests === 'yes' && (
                    <>
                      <TextField
                        label="Number of Guests"
                        name="guest_count"
                        type="number"
                        value={formData.guest_count}
                        onChange={(e) => {
                          const value = parseInt(e.target.value);
                          if (isNaN(value)) return;
                          const maxGuests = event?.max_guests_per_rsvp;
                          let newCount = value;
                          if (maxGuests !== undefined && maxGuests !== -1 && value > maxGuests) {
                            newCount = maxGuests;
                          }
                          if (newCount < 1) newCount = 1;
                          setFormData(prev => ({
                            ...prev, guest_count: newCount,
                            guest_names: Array(newCount).fill('').map((_, i) => prev.guest_names[i] || '')
                          }));
                        }}
                        fullWidth
                        required
                        inputProps={{
                          min: 1,
                          max: event?.max_guests_per_rsvp === -1 ? undefined : event?.max_guests_per_rsvp
                        }}
                        error={formData.guest_count < 1}
                        helperText={
                          formData.guest_count < 1 ? "Number of guests must be at least 1"
                            : event?.max_guests_per_rsvp === 0 ? "No additional guests allowed for this event"
                            : event?.max_guests_per_rsvp === -1 ? "No limit on number of guests"
                            : `Maximum ${event?.max_guests_per_rsvp} additional guests allowed`
                        }
                        disabled={isEventClosed}
                      />

                      {Array.from({ length: formData.guest_count }).map((_, index) => (
                        <TextField
                          key={index}
                          fullWidth
                          label={`Guest ${index + 1} Name`}
                          name={`guest_name_${index}`}
                          value={formData.guest_names[index] || ''}
                          onChange={handleChange}
                          required
                          disabled={isEventClosed}
                        />
                      ))}
                    </>
                  )}

                  {neededItems.length > 0 && (
                    <FormControl fullWidth disabled={isEventClosed}>
                      <InputLabel>What items are you bringing?</InputLabel>
                      <Select
                        multiple
                        name="items_bringing"
                        value={formData.items_bringing}
                        onChange={handleItemsChange}
                        input={<OutlinedInput label="What items are you bringing?" />}
                        renderValue={(selected) => (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {selected.map((value) => (
                              <Chip key={value} label={value} size="small" />
                            ))}
                          </Box>
                        )}
                        open={isItemsSelectOpen}
                        onOpen={() => setIsItemsSelectOpen(true)}
                        onClose={() => setIsItemsSelectOpen(false)}
                        MenuProps={{
                          PaperProps: { sx: { maxHeight: 300, overflowY: 'auto' } },
                        }}
                      >
                        {neededItems.map((item) => (
                          <MenuItem key={item} value={item}>
                            <Checkbox checked={formData.items_bringing.includes(item)} />
                            <ListItemText primary={item} />
                          </MenuItem>
                        ))}
                        <Box sx={{
                          position: 'sticky', bottom: 0, bgcolor: 'background.paper',
                          p: 1, borderTop: '1px solid', borderColor: 'divider', textAlign: 'center',
                        }}>
                          <Button
                            variant="contained"
                            onClick={() => setIsItemsSelectOpen(false)}
                            fullWidth
                            size="small"
                            disabled={isEventClosed}
                          >
                            Done
                          </Button>
                        </Box>
                      </Select>
                    </FormControl>
                  )}

                  <TextField
                    label="Any other item(s)?"
                    name="other_items"
                    value={formData.other_items}
                    onChange={handleChange}
                    fullWidth
                    multiline
                    rows={2}
                    placeholder="Enter any additional items you'd like to bring"
                    disabled={isEventClosed}
                  />
                </>
              )}

              {!isEventClosed && (
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  fullWidth
                  disabled={isSubmitting ||
                    !formData.name.trim() || !formData.email_address.trim() || !formData.attending ||
                    (formData.attending === 'yes' && !formData.bringing_guests) ||
                    (formData.bringing_guests === 'yes' && (formData.guest_count < 1 || formData.guest_names.some(n => !n.trim())))
                  }
                  sx={{ mt: 1 }}
                >
                  {isSubmitting ? 'Saving Changes...' : 'Save Changes'}
                </Button>
              )}
            </Stack>
          </Box>
        </Paper>
      </Container>
    </Box>
  );

  return eventTheme ? <ThemeProvider theme={eventTheme}>{mainContent}</ThemeProvider> : mainContent;
};

export default RSVPEditForm;
